import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  IconButton,
  CircularProgress,
  Tooltip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Divider
} from '@mui/material';
import {
  Refresh,
  PictureAsPdf,
  TableChart,
  FilterList,
  Clear
} from '@mui/icons-material';
import { TableSortLabel } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import EditorConvertToHTML from '../utils/TextEditor';

// --- EXPORT LIBRARIES ---
import XLSX from 'xlsx-js-style';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// --- ASSETS ---
import marabesLogo from '../assets/images/logos/Marabes.png';

// --- SERVICES ---
import { getAllContractRenewalFeedbacks, updateContractRenewalFeedback } from '../services/contractRenewalFeedbackAPIs';
import { getAllEmployees } from '../services/HRstaff';
import { getAllContracts } from '../services/ContractAPI';

if (typeof jsPDF.prototype.autoTable !== 'function') {
  jsPDF.prototype.autoTable = autoTable;
}

// === THEME ===
const THEME = {
  bg: '#F8FAFC',
  surface: '#FFFFFF',
  primary: '#1E293B',
  secondary: '#64748B',
  accent: '#3B82F6',
  border: '#E2E8F0',
  tableHeader: '#F1F5F9',
  danger: '#EF4444',
  warning: '#F59E0B',
  success: '#10B981',
  info: '#3B82F6',
};

const styles = {
  container: {
    minHeight: '100vh',
    padding: '20px',
    backgroundColor: THEME.bg,
    fontFamily: '"Inter", "Segoe UI", Roboto, sans-serif',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  headerCard: {
    backgroundColor: THEME.surface,
    padding: '20px',
    borderRadius: '12px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.03)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    border: `1px solid ${THEME.border}`,
    flexWrap: 'wrap',
    gap: '16px'
  },
  titleText: {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: THEME.primary,
  },
  subTitle: {
    fontSize: '0.875rem',
    color: THEME.secondary,
  },
  filtersCard: {
    backgroundColor: THEME.surface,
    padding: '16px',
    borderRadius: '12px',
    border: `1px solid ${THEME.border}`,
    boxShadow: '0 2px 4px rgba(0,0,0,0.03)',
    display: 'flex',
    gap: '16px',
    alignItems: 'center',
    flexWrap: 'wrap'
  },
  tableCard: {
    backgroundColor: THEME.surface,
    borderRadius: '12px',
    border: `1px solid ${THEME.border}`,
    overflowX: 'auto',
    boxShadow: '0 2px 4px rgba(0,0,0,0.03)',
  },
  th: {
    backgroundColor: THEME.tableHeader,
    color: THEME.primary,
    fontWeight: '700',
    fontSize: '0.65rem',
    textTransform: 'uppercase',
    padding: '12px 16px',
    borderBottom: `1px solid ${THEME.border}`,
  },
  td: {
    padding: '12px 16px',
    color: '#334155',
    fontSize: '0.875rem',
    borderBottom: `1px solid ${THEME.border}`,
  },
  exportBtn: {
    borderColor: THEME.border,
    color: THEME.secondary,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.85rem',
    padding: '6px 12px',
    '&:hover': {
      borderColor: THEME.accent,
      color: THEME.accent,
      backgroundColor: '#EFF6FF'
    }
  }
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return format(date, 'dd-MM-yyyy');
  } catch {
    return 'Invalid Date';
  }
};

const parseDate = (ds) => {
  if (!ds) return 0;
  if (ds instanceof Date) return isNaN(ds.getTime()) ? 0 : ds.getTime();
  const str = String(ds).trim();
  if (!str) return 0;
  const d = new Date(str);
  return isNaN(d.getTime()) ? 0 : d.getTime();
};

const getExpirationHighlight = (dateString) => {
  if (!dateString) return null;
  try {
    const endDate = new Date(dateString);
    if (isNaN(endDate.getTime())) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffTime = endDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays <= 30) {
      return 'red';
    } else if (diffDays > 30 && diffDays <= 60) {
      return 'yellow';
    }
  } catch {
    return null;
  }
  return null;
};

const isHtml = (str) => typeof str === 'string' && /<[a-z][\s\S]*>/i.test(str);

const stripHtml = (html) => {
  if (!html) return '';
  let processed = html
    .replace(/<br\s*[\/]?>/gi, '\n')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/&nbsp;/g, ' ');
  const tmp = document.createElement('div');
  tmp.innerHTML = processed;
  return (tmp.textContent || tmp.innerText || '').trim();
};

const FeedbackCell = ({ value, onClick }) => {
  if (value === null || value === undefined || value === '') {
    return (
      <Box
        onClick={onClick}
        sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#F8FAFC' }, borderRadius: '4px', px: 0.5, py: 0.25, minHeight: 32, display: 'flex', alignItems: 'center' }}
      >
        <span style={{ fontStyle: 'italic', color: '#94a3b8', fontSize: '0.8rem' }}>No feedback</span>
      </Box>
    );
  }
  const text = String(value);
  const plainText = isHtml(text) ? stripHtml(text) : text;

  return (
    <Box
      onClick={onClick}
      sx={{
        cursor: 'pointer',
        '&:hover': { bgcolor: '#F1F5F9' },
        borderRadius: '4px',
        px: 0.5,
        py: 0.25,
        minHeight: 32,
      }}
    >
      <Typography
        sx={{
          fontSize: '0.8rem',
          lineHeight: 1.4,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          color: '#334155',
        }}
      >
        {plainText}
      </Typography>
      {plainText.length > 60 && (
        <Typography sx={{ fontSize: '0.7rem', color: '#3B82F6', mt: 0.25, fontWeight: 600 }}>
          View more
        </Typography>
      )}
    </Box>
  );
};

const toDisplayText = (value, fallback = 'N/A') => {
  if (value === null || value === undefined || value === '') return fallback;

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  if (Array.isArray(value)) {
    const items = value.map((item) => toDisplayText(item, '')).filter(Boolean);
    return items.length ? items.join(', ') : fallback;
  }

  if (typeof value === 'object') {
    return (
      value.name ||
      value.fullName ||
      value.label ||
      value.title ||
      value.value ||
      (value.id !== undefined ? String(value.id) : fallback)
    );
  }

  return fallback;
};

const toPlainText = (value, fallback = 'N/A') => {
  const text = toDisplayText(value, fallback);
  return isHtml(text) ? stripHtml(text) : text;
};

const getBase64FromUrl = (url) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.setAttribute('crossOrigin', 'anonymous');
    img.src = url;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => {
      resolve(null);
    };
  });
};

const ContractRenewalFeedbackReport = () => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Filters
  const [officeFilter, setOfficeFilter] = useState('ALL');
  const [deptFilter, setDeptFilter] = useState('ALL');
  const [positionFilter, setPositionFilter] = useState('ALL');
  const [projectFilter, setProjectFilter] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [startDateFilter, setStartDateFilter] = useState(null);
  const [endDateFilter, setEndDateFilter] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const handleSort = useCallback((key) => {
    setSortConfig(prev => {
      if (prev.key === key) {
        if (prev.direction === 'asc') return { key, direction: 'desc' };
        if (prev.direction === 'desc') return { key: null, direction: 'asc' };
        return { key, direction: 'asc' };
      }
      return { key, direction: 'asc' };
    });
  }, []);

  const TABLE_COLUMNS = [
    { id: 'employeeName', label: 'Employee', minWidth: 150 },
    { id: 'startWorkingDate', label: 'Start Working Date', minWidth: 130 },
    { id: 'contractStartDate', label: 'Contract Start Date', minWidth: 130 },
    { id: 'contractEndDate', label: 'End Date' },
    { id: 'contractType', label: 'Type' },
    { id: 'numberOfExtensions', label: 'Max Ext.' },
    { id: 'status', label: 'Status' },
    { id: 'office', label: 'Office' },
    { id: 'department', label: 'Department' },
    { id: 'position', label: 'Position' },
    { id: 'projects', label: 'Projects' },
    { id: 'managerFeedback', label: 'Manager Feedback' },
    { id: 'projectManagerFeedback', label: 'PM Feedback' },
    { id: 'hrFeedback', label: 'HR Feedback' },
    { id: 'actionRequired', label: 'Action Required' },
    { id: 'finalExtensionPossible', label: 'Final Possible' }
  ];

  const ALL_EXPORT_COLUMNS = useMemo(() => [
    { key: 'employeeName', label: 'Employee Name' },
    { key: 'startWorkingDate', label: 'Start working date' },
    { key: 'contractStartDate', label: 'Contract Start date' },
    { key: 'contractEndDate', label: 'End Date' },
    { key: 'contractType', label: 'Contract Type' },
    { key: 'numberOfExtensions', label: 'Max Extensions' },
    { key: 'status', label: 'Contract Status' },
    { key: 'office', label: 'Office' },
    { key: 'department', label: 'Department' },
    { key: 'position', label: 'Position' },
    { key: 'projects', label: 'Projects' },
    { key: 'managerFeedback', label: 'Manager Feedback' },
    { key: 'projectManagerFeedback', label: 'Project Manager Feedback' },
    { key: 'hrFeedback', label: 'HR Feedback' },
    { key: 'actionRequired', label: 'Action Required' },
    { key: 'finalExtensionPossible', label: 'Final Possible' }
  ], []);

  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportType, setExportType] = useState(null); // 'excel' | 'pdf'

  // Feedback view/edit dialog
  const [feedbackDialog, setFeedbackDialog] = useState({ open: false, rowId: null, field: null, label: '', value: '' });
  const [feedbackEditValue, setFeedbackEditValue] = useState('');
  const [feedbackEditing, setFeedbackEditing] = useState(false);
  const [feedbackSaving, setFeedbackSaving] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState({
    employeeName: true,
    startWorkingDate: true,
    contractStartDate: true,
    contractEndDate: true,
    contractType: true,
    status: true,
    office: true,
    department: true,
    position: true,
    projects: true,
    managerFeedback: true,
    projectManagerFeedback: true,
    hrFeedback: true,
    actionRequired: true,
    numberOfExtensions: true,
    finalExtensionPossible: true
  });

  const handleSelectAllColumns = () => {
    const updated = {};
    ALL_EXPORT_COLUMNS.forEach(col => {
      updated[col.key] = true;
    });
    setSelectedColumns(updated);
  };

  const handleDeselectAllColumns = () => {
    const updated = {};
    ALL_EXPORT_COLUMNS.forEach(col => {
      updated[col.key] = false;
    });
    setSelectedColumns(updated);
  };

  // --- Feedback view / edit ---
  const FEEDBACK_FIELD_LABELS = {
    managerFeedback: 'Manager Feedback',
    projectManagerFeedback: 'Project Manager Feedback',
    hrFeedback: 'HR Feedback',
    actionRequired: 'Action Required',
  };

  const handleOpenFeedback = (rowId, field, label, value) => {
    setFeedbackDialog({ open: true, rowId, field, label, value: value || '' });
    setFeedbackEditValue(value || '');
    setFeedbackEditing(false);
  };

  const handleCloseFeedback = () => {
    setFeedbackDialog({ open: false, rowId: null, field: null, label: '', value: '' });
    setFeedbackEditing(false);
    setFeedbackSaving(false);
  };

  const handleSaveFeedback = async () => {
    const { rowId, field } = feedbackDialog;
    if (!rowId || !field) return;
    setFeedbackSaving(true);
    try {
      const payload = { [field]: feedbackEditValue || null };
      await updateContractRenewalFeedback(rowId, payload);

      setFeedbacks(prev => prev.map(fb => {
        if ((fb.id || fb.feedbackId) === rowId) {
          return { ...fb, [field]: feedbackEditValue || null };
        }
        return fb;
      }));

      setFeedbackDialog(prev => ({ ...prev, value: feedbackEditValue }));
      setFeedbackEditing(false);
    } catch (err) {
      console.error('Failed to update feedback', err);
      alert('Failed to save feedback.');
    } finally {
      setFeedbackSaving(false);
    }
  };

  const handleExcelClick = () => {
    setExportType('excel');
    setExportDialogOpen(true);
  };

  const handlePDFClick = () => {
    setExportType('pdf');
    setExportDialogOpen(true);
  };

  const handleConfirmExport = () => {
    const hasSelection = Object.values(selectedColumns).some(val => val);
    if (!hasSelection) {
      alert('Please select at least one column to export.');
      return;
    }
    setExportDialogOpen(false);
    if (exportType === 'excel') {
      handleExportExcel();
    } else if (exportType === 'pdf') {
      handleExportPDF();
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [feedbacksData, employeesData, contractsData] = await Promise.all([
        getAllContractRenewalFeedbacks(),
        getAllEmployees(),
        getAllContracts()
      ]);
      console.log('Fetched feedbacks:', feedbacksData);
      setFeedbacks(feedbacksData || []);
      setEmployees(employeesData || []);
      setContracts(contractsData || []);
    } catch (error) {
      console.error('Failed to load contract renewal feedbacks report data', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Map contracts / feedbacks to employee details
  const enrichedFeedbacks = useMemo(() => {
    const calculateContractDetails = (employeeName, currentType, contractId, empAllContracts, contractStartDate) => {
      const fullName = String(employeeName || '').replace(/\s+/g, ' ').trim().toLowerCase();

      // Special employee override
      if (fullName === 'oumaima attaya' || fullName === 'rania ramchi') {
        return {
          numberOfExtensions: 'N/A',
          finalExtensionPossible: 'CDI'
        };
      }

      const normType = String(currentType || '').toUpperCase().trim();

      // Find the previous expired/old contract type
      let previousExpiredType = null;
      if (empAllContracts && empAllContracts.length > 0 && contractStartDate) {
        const curStart = new Date(contractStartDate);
        const pastContracts = empAllContracts.filter(c => {
          const cId = c.contractId || c.id;
          if (cId && contractId && String(cId) === String(contractId)) return false;
          if (c.startDate) {
            return new Date(c.startDate) < curStart;
          }
          return false;
        });

        if (pastContracts.length > 0) {
          // Sort past contracts descending by start date
          pastContracts.sort((a, b) => {
            const dateA = a.startDate ? new Date(a.startDate) : new Date(0);
            const dateB = b.startDate ? new Date(b.startDate) : new Date(0);
            return dateB - dateA;
          });
          previousExpiredType = String(pastContracts[0].type || '').toUpperCase().trim();
        }
      }

      // Calculate max extensions and final possible
      let maxExtensions = 'N/A';
      let finalExtensionPossible = 'N/A';

      switch (normType) {
        case 'ANAPEC':
          maxExtensions = 2;
          finalExtensionPossible = 'CDD';
          break;
        case 'CDD':
          if (previousExpiredType === 'CDD') {
            maxExtensions = 'N/A';
            finalExtensionPossible = 'CDI';
          } else {
            maxExtensions = 1;
            finalExtensionPossible = 'CDD';
          }
          break;
        case 'CDI':
          maxExtensions = 'N/A';
          finalExtensionPossible = 'N/A';
          break;
        case 'INTERNSHIP':
          maxExtensions = 1;
          finalExtensionPossible = 'CDI, CDD, ANAPEC';
          break;
        case 'PCS':
          maxExtensions = 0;
          finalExtensionPossible = 'CDI, CDD';
          break;
        default:
          maxExtensions = 0;
          finalExtensionPossible = currentType || 'N/A';
      }

      return {
        numberOfExtensions: maxExtensions,
        finalExtensionPossible
      };
    };

    const resolveEmpNameFromContract = (c) => {
      if (c.employeeName) return toDisplayText(c.employeeName, '').trim().toLowerCase();
      if (c.employeeId) {
        const emp = employees.find(e => e.employeeId === c.employeeId || e.id === c.employeeId);
        if (emp) return `${emp.firstName || ''} ${emp.lastName || ''}`.trim().toLowerCase();
      }
      return '';
    };

    const dateKey = (ds) => {
      if (!ds) return '';
      const ts = parseDate(ds);
      return ts ? new Date(ts).toISOString().slice(0, 10) : '';
    };

    const contractKey = (empName, startDate, endDate) =>
      `${empName}__${dateKey(startDate)}__${dateKey(endDate)}`;

    // Build a map of feedbacks keyed by employee name + contract dates
    const fbByKey = new Map();
    feedbacks.forEach(fb => {
      const empName = toDisplayText(fb.employeeName, '').trim().toLowerCase();
      const key = contractKey(empName, fb.contractStartDate, fb.contractEndDate);
      if (key !== '____') fbByKey.set(key, fb);
    });

    const targetContracts = [];
    const processedFeedbackKeys = new Set();

    // Identify employees that currently have an ACTIVE contract
    const employeesWithActiveContract = new Set();
    contracts.forEach(c => {
      if (String(c.status).toUpperCase() === 'ACTIVE') {
        employeesWithActiveContract.add(resolveEmpNameFromContract(c));
      }
    });

    contracts.forEach(c => {
      const isContractActive = String(c.status).toUpperCase() === 'ACTIVE';
      const empName = resolveEmpNameFromContract(c);
      const key = contractKey(empName, c.startDate, c.endDate);
      const fb = fbByKey.get(key) || null;

      // Filter out expired contracts if the employee already has an active one
      if (!isContractActive && employeesWithActiveContract.has(empName)) {
        if (fb) processedFeedbackKeys.add(key); // prevent fallback loop from adding it
        return; 
      }

      if (fb) processedFeedbackKeys.add(key);

      if (isContractActive || fb) {
        targetContracts.push({ contract: c, feedback: fb });
      }
    });

    // Add any feedbacks not matched to a contract
    feedbacks.forEach(fb => {
      const empName = toDisplayText(fb.employeeName, '').trim().toLowerCase();
      const key = contractKey(empName, fb.contractStartDate, fb.contractEndDate);
      if (!processedFeedbackKeys.has(key)) {
        // Also filter out unmatched feedback for expired contracts if they have an active one
        if (employeesWithActiveContract.has(empName)) {
          return;
        }
        targetContracts.push({ contract: null, feedback: fb });
      }
    });

    return targetContracts.map(({ contract, feedback }) => {
      let employeeName = '';
      let matchedEmp = null;

      if (feedback) {
        employeeName = toDisplayText(feedback.employeeName, '');
      } else if (contract) {
        if (contract.employeeName) {
          employeeName = toDisplayText(contract.employeeName, '');
        } else if (contract.employeeId) {
          const emp = employees.find(e => e.employeeId === contract.employeeId || e.id === contract.employeeId);
          if (emp) {
            employeeName = `${emp.firstName || ''} ${emp.lastName || ''}`.trim();
          }
        }
      }

      if (employeeName) {
        matchedEmp = employees.find(emp => {
          const fullName = `${emp.firstName || ''} ${emp.lastName || ''}`.trim().toLowerCase();
          return fullName === employeeName.trim().toLowerCase();
        });
      } else if (contract && contract.employeeId) {
        matchedEmp = employees.find(e => e.employeeId === contract.employeeId || e.id === contract.employeeId);
        if (matchedEmp) {
          employeeName = `${matchedEmp.firstName || ''} ${matchedEmp.lastName || ''}`.trim();
        }
      }

      const toIsoDate = (ds) => {
        if (!ds) return null;
        const ts = parseDate(ds);
        return ts ? new Date(ts).toISOString().slice(0, 10) : null;
      };

      const contractStartDate = toIsoDate(contract?.startDate || feedback?.contractStartDate);
      const contractEndDate = toIsoDate(contract?.endDate || feedback?.contractEndDate);
      const contractType = contract?.type || feedback?.contractType || 'N/A';

      // Position: prefer feedback, fallback to employee
      let positionText = 'N/A';
      if (feedback && Array.isArray(feedback.positions) && feedback.positions.length > 0) {
        positionText = feedback.positions.join(', ');
      } else if (matchedEmp) {
        let posArr = [];
        if (Array.isArray(matchedEmp.positions) && matchedEmp.positions.length > 0) {
          posArr = matchedEmp.positions.map(p => ({ positionName: p.positionName, fte: p.fte != null ? Number(p.fte) : 1.0 }));
        } else {
          posArr = [{ positionName: matchedEmp.position || '', fte: 1.0 }];
          if (matchedEmp.secondposition && matchedEmp.secondposition.trim() !== '') {
            posArr.push({ positionName: matchedEmp.secondposition, fte: 0.5 });
          }
        }
        const filteredPos = posArr.filter(p => p.positionName);
        if (filteredPos.length > 0) {
          positionText = filteredPos.map(p => `${p.positionName} (${p.fte})`).join('; ');
        } else if (matchedEmp.positionTitle) {
          positionText = matchedEmp.positionTitle;
        }
      }

      // Projects: prefer feedback, fallback to employee
      let projectsText = 'N/A';
      if (feedback && Array.isArray(feedback.projects) && feedback.projects.length > 0) {
        projectsText = feedback.projects.join(' - ');
      } else if (matchedEmp) {
        if (matchedEmp.projects && matchedEmp.projects.length > 0) {
          projectsText = matchedEmp.projects.map(p => typeof p === 'string' ? p : p.projectName).filter(Boolean).join(' - ');
        } else {
          const projs = [];
          if (matchedEmp.projectName && matchedEmp.projectName.trim() !== '' && matchedEmp.projectName !== 'N/A') projs.push(matchedEmp.projectName.trim());
          if (matchedEmp.secondProjectName && matchedEmp.secondProjectName.trim() !== '' && matchedEmp.secondProjectName !== 'N/A') projs.push(matchedEmp.secondProjectName.trim());
          if (projs.length > 0) projectsText = projs.join(' - ');
        }
      }

      // Department: prefer feedback, fallback to employee
      let departmentText = feedback?.department || (matchedEmp?.departmentName || 'N/A');

      // Office: from employee
      let officeText = 'N/A';
      if (matchedEmp) {
        if (matchedEmp.officeName) officeText = matchedEmp.officeName ;
        else if (typeof matchedEmp.office === 'string') officeText = matchedEmp.office;
        else if (matchedEmp.department?.location) officeText = matchedEmp.department.location;
        else if (matchedEmp.personalInfo?.office?.name) officeText = matchedEmp.personalInfo.office.name;
        else if (typeof matchedEmp.personalInfo?.office === 'string') officeText = matchedEmp.personalInfo.office;
      }

      // Status: prefer contract, fallback to feedback
      let contractStatus = contract?.status || feedback?.status || 'UNKNOWN';

      const empId = matchedEmp?.id || matchedEmp?.employeeId || contract?.employeeId || feedback?.employeeId;
      const empAllContracts = contracts.filter(c => {
        if (empId && (c.employeeId === empId)) return true;
        if (employeeName && c.employeeName && c.employeeName.trim().toLowerCase() === employeeName.trim().toLowerCase()) return true;
        return false;
      });

      const calculated = calculateContractDetails(
        employeeName,
        contractType,
        contract?.contractId || contract?.id || feedback?.contractId,
        empAllContracts,
        contractStartDate
      );

      return {
        id: feedback?.id || null,
        contractId: contract?.contractId || contract?.id || feedback?.contractId || null,
        employeeName,
        startWorkingDate: toIsoDate(matchedEmp?.dateOfJoining || matchedEmp?.joiningDate || matchedEmp?.startDate || null),
        contractStartDate,
        contractEndDate,
        contractType,
        status: String(contractStatus).toUpperCase(),
        office: officeText,
        department: departmentText,
        position: positionText,
        projects: projectsText,
        managerFeedback: feedback?.managerFeedback || null,
        projectManagerFeedback: feedback?.projectManagerFeedback || null,
        hrFeedback: feedback?.hrFeedback || null,
        actionRequired: feedback?.actionRequired || null,
        numberOfExtensions: calculated.numberOfExtensions,
        finalExtensionPossible: calculated.finalExtensionPossible
      };
    });
  }, [feedbacks, employees, contracts]);

  // Derive unique filter lists
  const offices = ['ALL', 'Nador', 'Tangier'];

  const departments = useMemo(() => {
    const list = enrichedFeedbacks.map(f => f.department).filter(Boolean).filter(d => d !== 'N/A');
    const set = new Set(list);
    const standardDepts = ['IT', 'HR', 'Finance', 'Operations', 'Marketing', 'Sales', 'Customer Support', 'Administration'];
    standardDepts.forEach(d => set.add(d));
    return ['ALL', ...Array.from(set)].sort();
  }, [enrichedFeedbacks]);

  const positions = useMemo(() => {
    const list = enrichedFeedbacks.map(f => f.position).filter(Boolean).filter(p => p !== 'N/A');
    const set = new Set();
    list.forEach(item => {
      item.split('; ').forEach(p => {
        const name = p.replace(/\s*\([\d.]+\)$/, '');
        set.add(name);
      });
    });
    const standardPositions = [
      'Software Engineer',
      'Developer',
      'Project Manager',
      'HR Manager',
      'HR',
      'Manager',
      'Account Manager',
      'Customer Service',
      'Bookkeeper',
      'Planner',
      'IT Support',
      'IT Professional',
      'Facility'
    ];
    standardPositions.forEach(p => set.add(p));
    return ['ALL', ...Array.from(set)].sort();
  }, [enrichedFeedbacks]);

  const projectFiltersList = useMemo(() => {
    const list = enrichedFeedbacks.map(f => f.projects).filter(Boolean).filter(p => p !== 'N/A');
    const set = new Set();
    list.forEach(item => {
      item.split(' - ').forEach(p => set.add(p.trim()));
    });
    return ['ALL', ...Array.from(set)].sort();
  }, [enrichedFeedbacks]);

  // Apply filters
  const rawFilteredFeedbacks = useMemo(() => {
    return enrichedFeedbacks.filter(fb => {
      const matchesOffice = officeFilter === 'ALL' || fb.office === officeFilter;
      const matchesDept = deptFilter === 'ALL' || fb.department === deptFilter;
      const matchesPos = positionFilter === 'ALL' || fb.position.includes(positionFilter);
      const matchesProject = projectFilter === 'ALL' || fb.projects.includes(projectFilter);
      const matchesSearch = searchQuery === '' ||
        toDisplayText(fb.employeeName, '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        toDisplayText(fb.contractType, '').toLowerCase().includes(searchQuery.toLowerCase());

      // Filter for expiration date range
      let matchesExpiration = true;
      if (startDateFilter || endDateFilter) {
        if (!fb.contractEndDate || String(fb.contractType).toUpperCase() === 'CDI') {
          matchesExpiration = false;
        } else {
          const cEnd = new Date(fb.contractEndDate);
          cEnd.setHours(0, 0, 0, 0);

          if (startDateFilter) {
            const sDate = new Date(startDateFilter);
            sDate.setHours(0, 0, 0, 0);
            if (cEnd < sDate) matchesExpiration = false;
          }
          if (endDateFilter) {
            const eDate = new Date(endDateFilter);
            eDate.setHours(23, 59, 59, 999);
            if (cEnd > eDate) matchesExpiration = false;
          }
        }
      }

      return matchesOffice && matchesDept && matchesPos && matchesProject && matchesSearch && matchesExpiration;
    });
  }, [enrichedFeedbacks, officeFilter, deptFilter, positionFilter, projectFilter, searchQuery, startDateFilter, endDateFilter]);

  const filteredFeedbacks = useMemo(() => {
    if (!sortConfig.key) return rawFilteredFeedbacks;

    return [...rawFilteredFeedbacks].sort((a, b) => {
      // Force CDI / missing end dates to always be at the bottom when sorting by contractEndDate
      if (sortConfig.key === 'contractEndDate') {
        const aNoDate = String(a.contractType).toUpperCase() === 'CDI' || !a.contractEndDate;
        const bNoDate = String(b.contractType).toUpperCase() === 'CDI' || !b.contractEndDate;
        if (aNoDate && !bNoDate) return 1;
        if (!aNoDate && bNoDate) return -1;
      }

      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      if (sortConfig.key === 'startWorkingDate' || sortConfig.key === 'contractStartDate' || sortConfig.key === 'contractEndDate') {
        aVal = parseDate(aVal);
        bVal = parseDate(bVal);
      } else if (sortConfig.key === 'numberOfExtensions') {
        if (aVal === 'N/A' || aVal == null) aVal = -1;
        if (bVal === 'N/A' || bVal == null) bVal = -1;
        aVal = Number(aVal);
        bVal = Number(bVal);
      } else {
        aVal = toDisplayText(aVal, '').toLowerCase();
        bVal = toDisplayText(bVal, '').toLowerCase();
      }

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [rawFilteredFeedbacks, sortConfig]);

  // PDF Export
  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const doc = new jsPDF('l', 'mm', 'a4');
      const margin = 14;
      const pw = doc.internal.pageSize.width;

      const marabesBase64 = await getBase64FromUrl(marabesLogo);
      const axoBase64 = await getBase64FromUrl('/cLogo.png');

      if (marabesBase64) doc.addImage(marabesBase64, 'PNG', margin, 10, 30, 10);
      if (axoBase64) doc.addImage(axoBase64, 'PNG', pw - margin - 30, 10, 30, 10);

      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 41, 59);
      doc.text('Contract Renewal Feedbacks Report', pw / 2, 28, { align: 'center' });

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Generated at: ${format(new Date(), 'dd-MM-yyyy HH:mm')}`, margin, 38);

      const activeColumns = ALL_EXPORT_COLUMNS.filter(col => selectedColumns[col.key]);
      const headers = [activeColumns.map(col => col.label)];

      const body = filteredFeedbacks.map(fb => {
        return activeColumns.map(col => {
          if (col.key === 'startWorkingDate') {
            return formatDate(fb.startWorkingDate);
          }
          if (col.key === 'contractStartDate') {
            return formatDate(fb.contractStartDate);
          }
          if (col.key === 'contractEndDate') {
            const isCDI = String(fb.contractType).toUpperCase() === 'CDI';
            return isCDI ? '-' : formatDate(fb.contractEndDate);
          }
          if (col.key === 'managerFeedback') {
            return toPlainText(fb.managerFeedback, '-');
          }
          if (col.key === 'projectManagerFeedback') {
            return toPlainText(fb.projectManagerFeedback, '-');
          }
          if (col.key === 'hrFeedback') {
            return toPlainText(fb.hrFeedback, '-');
          }
          if (col.key === 'actionRequired') {
            return toPlainText(fb.actionRequired, '-');
          }
          if (col.key === 'numberOfExtensions') {
            if (fb.contractType === 'CDI' || fb.type === 'CDI' || fb.numberOfExtensions === 'N/A' || fb.numberOfExtensions === '-' || !fb.numberOfExtensions) return '0';
            return fb.numberOfExtensions;
          }
          if (col.key === 'finalExtensionPossible') {
            return toDisplayText(fb.finalExtensionPossible, '-');
          }
          return toDisplayText(fb[col.key]);
        });
      });

      const colStyles = {};
      activeColumns.forEach((col, idx) => {
        if (['managerFeedback', 'projectManagerFeedback', 'hrFeedback', 'actionRequired'].includes(col.key)) {
          colStyles[idx] = { cellWidth: 20 };
        } else if (['startWorkingDate', 'contractStartDate', 'contractEndDate'].includes(col.key)) {
          colStyles[idx] = { cellWidth: 17 };
        }
      });

      autoTable(doc, {
        startY: 44,
        head: headers,
        body: body,
        theme: 'grid',
        showHead: 'firstPage',
        rowPageBreak: 'avoid',
        styles: { fontSize: 7, cellPadding: 1.5 },
        headStyles: { fillColor: [30, 41, 59], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 6 },
        columnStyles: colStyles,
        didParseCell: (data) => {
          if (data.section === 'body') {
            const fb = filteredFeedbacks[data.row.index];
            if (fb) {
              const isCDI = String(fb.contractType).toUpperCase() === 'CDI';
              if (!isCDI) {
                const highlight = getExpirationHighlight(fb.contractEndDate);
                if (highlight === 'red') {
                  data.cell.styles.fillColor = [254, 226, 226];
                  data.cell.styles.textColor = [153, 27, 27];
                  data.cell.styles.fontStyle = 'bold';
                } else if (highlight === 'yellow') {
                  data.cell.styles.fillColor = [254, 243, 199];
                  data.cell.styles.textColor = [146, 64, 14];
                  data.cell.styles.fontStyle = 'bold';
                }
              }
            }
          }
        }
      });

      doc.save(`Contract_Renewal_Feedbacks_Report_${format(new Date(), 'dd-MM-yyyy')}.pdf`);
    } catch (e) {
      console.error(e);
      alert('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  // Excel Export
  const handleExportExcel = () => {
    if (!filteredFeedbacks.length) return;

    const activeColumns = ALL_EXPORT_COLUMNS.filter(col => selectedColumns[col.key]);
    const rows = filteredFeedbacks.map(fb => {
      const row = {};
      activeColumns.forEach(col => {
        if (col.key === 'startWorkingDate') {
          row[col.label] = formatDate(fb.startWorkingDate);
        } else if (col.key === 'contractStartDate') {
          row[col.label] = formatDate(fb.contractStartDate);
        } else if (col.key === 'contractEndDate') {
          const isCDI = String(fb.contractType).toUpperCase() === 'CDI';
          row[col.label] = isCDI ? '-' : formatDate(fb.contractEndDate);
        } else if (col.key === 'managerFeedback') {
          row[col.label] = toPlainText(fb.managerFeedback, '');
        } else if (col.key === 'projectManagerFeedback') {
          row[col.label] = toPlainText(fb.projectManagerFeedback, '');
        } else if (col.key === 'hrFeedback') {
          row[col.label] = toPlainText(fb.hrFeedback, '');
        } else if (col.key === 'actionRequired') {
          row[col.label] = toPlainText(fb.actionRequired, '');
        } else if (col.key === 'numberOfExtensions') {
          if (fb.contractType === 'CDI' || fb.type === 'CDI' || fb.numberOfExtensions === 'N/A' || fb.numberOfExtensions === '-' || !fb.numberOfExtensions) {
            row[col.label] = '0';
          } else {
            row[col.label] = fb.numberOfExtensions;
          }
        } else if (col.key === 'finalExtensionPossible') {
          row[col.label] = toDisplayText(fb.finalExtensionPossible, '');
        } else {
          row[col.label] = toDisplayText(fb[col.key]);
        }
      });
      return row;
    });

    const ws = XLSX.utils.json_to_sheet(rows);

    // Apply custom styling for cells, headers, borders and background colors
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let R = range.s.r; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cell_address = { c: C, r: R };
        const cell_ref = XLSX.utils.encode_cell(cell_address);
        if (!ws[cell_ref]) continue;

        const isHeader = R === 0;
        if (isHeader) {
          // Sleek dark-slate header styling with white bold text
          ws[cell_ref].s = {
            font: { name: 'Segoe UI', sz: 9, bold: true, color: { rgb: 'FFFFFF' } },
            fill: { fgColor: { rgb: '1E293B' } }, // THEME.primary
            alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
            border: {
              top: { style: 'medium', color: { rgb: '0F172A' } },
              bottom: { style: 'medium', color: { rgb: '0F172A' } },
              left: { style: 'thin', color: { rgb: '475569' } },
              right: { style: 'thin', color: { rgb: '475569' } }
            }
          };
        } else {
          // Data rows styling
          const fbIndex = R - 1;
          const fb = filteredFeedbacks[fbIndex];
          const isCDI = fb ? String(fb.contractType).toUpperCase() === 'CDI' : false;
          const highlight = (fb && !isCDI) ? getExpirationHighlight(fb.contractEndDate) : null;

          let cellFillColor = 'FFFFFF';
          let cellTextColor = '334155';
          let isBold = false;

          if (highlight === 'red') {
            cellFillColor = 'FEE2E2'; // Soft red matching UI
            cellTextColor = '991B1B';
            isBold = true;
          } else if (highlight === 'yellow') {
            cellFillColor = 'FEF3C7'; // Soft yellow matching UI
            cellTextColor = '92400E';
            isBold = true;
          }

          // Center-align certain metadata columns for a cleaner layout
          const colKey = activeColumns[C]?.key;
          let alignment = { horizontal: 'left', vertical: 'center' };
          if (['startWorkingDate', 'contractStartDate', 'contractEndDate', 'contractType', 'status', 'office', 'numberOfExtensions', 'finalExtensionPossible'].includes(colKey)) {
            alignment = { horizontal: 'center', vertical: 'center' };
          }

          ws[cell_ref].s = {
            font: { name: 'Segoe UI', sz: 10, color: { rgb: cellTextColor }, bold: isBold },
            fill: { fgColor: { rgb: cellFillColor } },
            alignment: alignment,
            border: {
              top: { style: 'thin', color: { rgb: 'E2E8F0' } },
              bottom: { style: 'thin', color: { rgb: 'E2E8F0' } },
              left: { style: 'thin', color: { rgb: 'E2E8F0' } },
              right: { style: 'thin', color: { rgb: 'E2E8F0' } }
            }
          };
        }
      }
    }

    // Set custom column widths (auto-fit plus padding)
    const colWidths = activeColumns.map((col, idx) => {
      let maxLen = col.label.length;
      for (let R = 1; R <= range.e.r; ++R) {
        const cell_ref = XLSX.utils.encode_cell({ c: idx, r: R });
        if (ws[cell_ref] && ws[cell_ref].v) {
          const valStr = String(ws[cell_ref].v);
          if (valStr.length > maxLen) {
            maxLen = valStr.length;
          }
        }
      }
      return { wch: Math.min(Math.max(maxLen + 4, 12), 40) }; // cap between 12 and 40
    });
    ws['!cols'] = colWidths;

    // Set row heights: 28pt for header, 22pt for data rows
    const rowHeights = [];
    for (let R = 0; R <= range.e.r; ++R) {
      rowHeights.push({ hpt: R === 0 ? 28 : 22 });
    }
    ws['!rows'] = rowHeights;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Contract Renewals');
    XLSX.writeFile(wb, `Contract_Renewal_Feedbacks_${format(new Date(), 'dd-MM-yyyy')}.xlsx`);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={styles.container}>
        <Box sx={styles.headerCard}>
          <Box>
            <Typography sx={styles.titleText}>Contract Renewal Feedbacks</Typography>
            <Typography sx={styles.subTitle}>Analyze and track all contracts renewal evaluations and notes across the organization</Typography>
          </Box>
          <Box display="flex" gap={1.5}>
            <Button
              variant="outlined"
              onClick={handleExcelClick}
              startIcon={<TableChart />}
              sx={styles.exportBtn}
            >
              Excel
            </Button>
            <Button
              variant="contained"
              onClick={handlePDFClick}
              disabled={exporting}
              startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdf />}
              sx={{ bgcolor: THEME.primary, '&:hover': { bgcolor: '#334155' } }}
            >
              Export PDF
            </Button>
          </Box>
        </Box>

        {/* Filters */}
        <Box sx={styles.filtersCard}>
          <TextField
            size="small"
            label="Search Employee"
            variant="outlined"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            sx={{ minWidth: 200, bgcolor: 'background.paper' }}
          />

          <FormControl size="small" sx={{ minWidth: 160, bgcolor: 'background.paper' }}>
            <InputLabel>Office</InputLabel>
            <Select value={officeFilter} label="Office" onChange={e => setOfficeFilter(e.target.value)}>
              {offices.map(o => (
                <MenuItem key={o} value={o}>{o === 'ALL' ? 'All Offices' : o}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160, bgcolor: 'background.paper' }}>
            <InputLabel>Department</InputLabel>
            <Select value={deptFilter} label="Department" onChange={e => setDeptFilter(e.target.value)}>
              {departments.map(d => (
                <MenuItem key={d} value={d}>{d === 'ALL' ? 'All Departments' : d}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 180, bgcolor: 'background.paper' }}>
            <InputLabel>Position</InputLabel>
            <Select value={positionFilter} label="Position" onChange={e => setPositionFilter(e.target.value)}>
              {positions.map(p => (
                <MenuItem key={p} value={p}>{p === 'ALL' ? 'All Positions' : p}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 180, bgcolor: 'background.paper' }}>
            <InputLabel>Project</InputLabel>
            <Select value={projectFilter} label="Project" onChange={e => setProjectFilter(e.target.value)}>
              {projectFiltersList.map(p => (
                <MenuItem key={p} value={p}>{p === 'ALL' ? 'All Projects' : p}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <DatePicker
            label="End Date (From)"
            value={startDateFilter}
            onChange={v => setStartDateFilter(v)}
            format="dd/MM/yyyy"
            slotProps={{ textField: { size: 'small', sx: { minWidth: 160, bgcolor: 'background.paper' } } }}
          />
          <DatePicker
            label="End Date (To)"
            value={endDateFilter}
            onChange={v => setEndDateFilter(v)}
            format="dd/MM/yyyy"
            slotProps={{ textField: { size: 'small', sx: { minWidth: 160, bgcolor: 'background.paper' } } }}
          />
          {(startDateFilter || endDateFilter) && (
            <IconButton
              size="small"
              onClick={() => { setStartDateFilter(''); setEndDateFilter(''); }}
              color="error"
              title="Clear dates"
            >
              <Clear />
            </IconButton>
          )}

          <Button
            variant="outlined"
            onClick={loadData}
            startIcon={<Refresh />}
            sx={styles.exportBtn}
          >
            Refresh
          </Button>
        </Box>

        {/* Table */}
        <TableContainer component={Paper} sx={styles.tableCard}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={8}>
              <CircularProgress />
            </Box>
          ) : filteredFeedbacks.length === 0 ? (
            <Box py={8} textAlign="center">
              <Typography color="textSecondary">No contract renewal feedback records found.</Typography>
            </Box>
          ) : (
            <Table sx={{ minWidth: 1600 }}>
              <TableHead>
                <TableRow>
                  {TABLE_COLUMNS.map((col) => (
                    <TableCell key={col.id} sx={{ ...styles.th, minWidth: col.minWidth || 'auto' }}>
                      <TableSortLabel
                        active={sortConfig.key === col.id}
                        direction={sortConfig.key === col.id ? sortConfig.direction : 'asc'}
                        onClick={() => handleSort(col.id)}
                        sx={{
                          '&.MuiTableSortLabel-root': {
                            color: THEME.primary,
                          },
                          '&.MuiTableSortLabel-active': {
                            color: THEME.primary,
                          },
                          '& .MuiTableSortLabel-icon': {
                            color: `${THEME.primary} !important`,
                          }
                        }}
                      >
                        {col.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredFeedbacks.map((fb, index) => {
                  const isCDI = String(fb.contractType).toUpperCase() === 'CDI';
                  const highlight = isCDI ? null : getExpirationHighlight(fb.contractEndDate);
                  let rowBgColor = 'transparent';
                  if (highlight === 'red') {
                    rowBgColor = '#FEF2F2'; // Soft light-red background
                  } else if (highlight === 'yellow') {
                    rowBgColor = '#FFFBEB'; // Soft light-yellow background
                  }

                  return (
                    <TableRow
                      key={fb.id || `${fb.employeeName}-${fb.contractStartDate}-${fb.contractEndDate}-${index}`}
                      hover
                      sx={{
                        backgroundColor: rowBgColor,
                        '&:hover': {
                          backgroundColor: highlight === 'red' ? '#FEE2E2 !important' : highlight === 'yellow' ? '#FEF3C7 !important' : 'rgba(0, 0, 0, 0.04) !important'
                        }
                      }}
                    >
                      <TableCell sx={{ ...styles.td, fontWeight: 600 }}>{toDisplayText(fb.employeeName)}</TableCell>
                      <TableCell sx={styles.td}>{formatDate(fb.startWorkingDate)}</TableCell>
                      <TableCell sx={styles.td}>{formatDate(fb.contractStartDate)}</TableCell>
                      <TableCell sx={styles.td}>
                        {(() => {
                          if (isCDI) return '-';
                          if (highlight === 'red') {
                            return (
                              <Chip
                                label={formatDate(fb.contractEndDate)}
                                size="small"
                                sx={{
                                  backgroundColor: '#FEE2E2',
                                  color: '#991B1B',
                                  fontWeight: 'bold',
                                  borderRadius: '6px'
                                }}
                              />
                            );
                          } else if (highlight === 'yellow') {
                            return (
                              <Chip
                                label={formatDate(fb.contractEndDate)}
                                size="small"
                                sx={{
                                  backgroundColor: '#FEF3C7',
                                  color: '#92400E',
                                  fontWeight: 'bold',
                                  borderRadius: '6px'
                                }}
                              />
                            );
                          }
                          return formatDate(fb.contractEndDate);
                        })()}
                      </TableCell>
                      <TableCell sx={styles.td}>
                        <Chip label={toDisplayText(fb.contractType)} color="primary" size="small" variant="outlined" />
                      </TableCell>
                      <TableCell sx={styles.td}>
                        {fb.contractType === 'CDI' || fb.type === 'CDI' || fb.numberOfExtensions === 'N/A' || fb.numberOfExtensions === '-' || !fb.numberOfExtensions ? '0' : fb.numberOfExtensions}
                      </TableCell>
                      <TableCell sx={styles.td}>
                        <Chip label={toDisplayText(fb.status)} size="small" sx={{ backgroundColor: fb.status === 'ACTIVE' ? '#D1FAE5' : '#FEE2E2', color: fb.status === 'ACTIVE' ? '#065F46' : '#991B1B', fontWeight: 600 }} />
                      </TableCell>
                      <TableCell sx={styles.td}>{toDisplayText(fb.office)}</TableCell>
                      <TableCell sx={styles.td}>{toDisplayText(fb.department)}</TableCell>
                      <TableCell sx={styles.td}>{toDisplayText(fb.position)}</TableCell>
                      <TableCell sx={styles.td}>
                        <Tooltip title={toDisplayText(fb.projects)}>
                          <Box sx={{
                            maxWidth: '120px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>.
                            {toDisplayText(fb.projects)}
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={styles.td}>
                        <FeedbackCell
                          value={fb.managerFeedback}
                          onClick={() => handleOpenFeedback(fb.id, 'managerFeedback', 'Manager Feedback', fb.managerFeedback)}
                        />
                      </TableCell>
                      <TableCell sx={styles.td}>
                        <FeedbackCell
                          value={fb.projectManagerFeedback}
                          onClick={() => handleOpenFeedback(fb.id, 'projectManagerFeedback', 'Project Manager Feedback', fb.projectManagerFeedback)}
                        />
                      </TableCell>
                      <TableCell sx={styles.td}>
                        <FeedbackCell
                          value={fb.hrFeedback}
                          onClick={() => handleOpenFeedback(fb.id, 'hrFeedback', 'HR Feedback', fb.hrFeedback)}
                        />
                      </TableCell>
                      <TableCell sx={styles.td}>
                        <FeedbackCell
                          value={fb.actionRequired}
                          onClick={() => handleOpenFeedback(fb.id, 'actionRequired', 'Action Required', fb.actionRequired)}
                        />
                      </TableCell>

                      <TableCell sx={styles.td}>
                        {toDisplayText(fb.finalExtensionPossible, '-')}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        {/* Dynamic Export Columns Dialog */}
        <Dialog
          open={exportDialogOpen}
          onClose={() => setExportDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '16px',
              padding: '8px'
            }
          }}
        >
          <DialogTitle sx={{ fontWeight: 700, fontSize: '1.25rem', color: THEME.primary, pb: 1 }}>
            Export Columns Configuration
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Choose the columns you want to include in your {exportType === 'excel' ? 'Excel spreadsheet' : 'PDF document'}.
            </Typography>

            <Box display="flex" gap={1} mb={2}>
              <Button size="small" variant="outlined" onClick={handleSelectAllColumns} sx={{ textTransform: 'none', borderRadius: '6px' }}>
                Select All
              </Button>
              <Button size="small" variant="outlined" color="error" onClick={handleDeselectAllColumns} sx={{ textTransform: 'none', borderRadius: '6px' }}>
                Deselect All
              </Button>
            </Box>

            <Divider sx={{ my: 1.5 }} />

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5, mt: 2 }}>
              {ALL_EXPORT_COLUMNS.map((col) => (
                <FormControlLabel
                  key={col.key}
                  control={
                    <Checkbox
                      checked={!!selectedColumns[col.key]}
                      onChange={(e) => {
                        setSelectedColumns(prev => ({
                          ...prev,
                          [col.key]: e.target.checked
                        }));
                      }}
                      color="primary"
                    />
                  }
                  label={<Typography variant="body2" sx={{ fontWeight: 500 }}>{col.label}</Typography>}
                />
              ))}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: `1px solid ${THEME.border}`, mt: 2 }}>
            <Button
              onClick={() => setExportDialogOpen(false)}
              color="inherit"
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmExport}
              variant="contained"
              sx={{
                bgcolor: THEME.primary,
                '&:hover': { bgcolor: '#334155' },
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '8px',
                px: 3
              }}
            >
              Export
            </Button>
          </DialogActions>
        </Dialog>

        {/* Feedback View / Edit Dialog */}
        <Dialog
          open={feedbackDialog.open}
          onClose={handleCloseFeedback}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: '16px' } }}
        >
          <DialogTitle sx={{ fontWeight: 700, color: THEME.primary, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {feedbackDialog.label}
            {feedbackDialog.rowId && !feedbackEditing && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => { setFeedbackEditValue(feedbackDialog.value || ''); setFeedbackEditing(true); }}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '6px' }}
              >
                {feedbackDialog.value ? 'Edit' : 'Add'}
              </Button>
            )}
          </DialogTitle>
          <DialogContent dividers>
            {feedbackEditing ? (
              <Box sx={{ mt: 1 }}>
                <EditorConvertToHTML
                  value={feedbackEditValue}
                  onChange={(html) => setFeedbackEditValue(html)}
                />
              </Box>
            ) : (
              feedbackDialog.value ? (
                <Box
                  sx={{
                    fontSize: '0.9rem',
                    lineHeight: 1.7,
                    color: '#334155',
                    '& p': { m: '8px 0' },
                    '& br': { display: 'block', content: '""', mt: '6px' },
                    '& b, & strong': { fontWeight: 600 },
                  }}
                  dangerouslySetInnerHTML={{ __html: feedbackDialog.value }}
                />
              ) : (
                <Typography color="textSecondary" sx={{ fontStyle: 'italic', py: 2 }}>No feedback provided</Typography>
              )
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: `1px solid ${THEME.border}` }}>
            <Button
              onClick={handleCloseFeedback}
              color="inherit"
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {feedbackEditing ? 'Cancel' : 'Close'}
            </Button>
            {feedbackEditing && (
              <Button
                onClick={handleSaveFeedback}
                variant="contained"
                disabled={feedbackSaving}
                startIcon={feedbackSaving ? <CircularProgress size={14} color="inherit" /> : null}
                sx={{ textTransform: 'none', fontWeight: 600, bgcolor: THEME.primary, '&:hover': { bgcolor: '#334155' } }}
              >
                {feedbackSaving ? 'Saving...' : 'Save'}
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default ContractRenewalFeedbackReport;
